import { decrypt, encrypt } from "../utils/other";
import { getTokenObj, isNewerToken, tokenUserId } from "./tokenManager";
import { saveSession } from "../utils/saveSession";
import {
  refreshTokens,
  isGenuineRefreshFailure,
  isRefreshRaceLost,
} from "./refreshTokens";
import { getCookie } from "../utils/cookieUtils";
import sessionExpired from "../Core/sessionExpired";

/**
 * Deduplicating token refresh coordinator for MFE mode.
 *
 * Both portal's own API interceptor (createApiClient.js) and Sandbox's
 * delegated refresh (via onTokenRefreshNeeded → handleSandboxTokenRefresh)
 * call this function. Because a single _mfeRefreshPromise is shared, only
 * ONE SSO call fires regardless of how many concurrent 401s occur across
 * both apps.
 *
 * On failure:
 *   - Calls sessionExpired() exactly once (portal's swal dialog).
 *   - Re-throws with err.sessionExpiredHandled = true so callers
 *     (sandbox's and portal's catch blocks) skip their own sessionExpired()
 *     calls and avoid double dialogs or conflicting window.location navigations.
 *
 * HOW TO REMOVE (when retiring the MFE):
 *   1. Delete this file.
 *   2. In SandboxRenderer.jsx, delete the import and restore the old
 *      handleSandboxTokenRefresh body (or just delete the whole block and
 *      remove the onTokenRefreshNeeded prop from mount()).
 *   3. In createApiClient.js, remove the import and the
 *      `if (window.__SANDBOX_IS_PLUGIN__)` branch — keep only the else branch.
 */
let _mfeRefreshPromise = null;

// Persists a fresh token pair to every store both apps read: localStorage.user
// (via the common saveSession, which also re-writes the shared cookies for
// students), the embedded Sandbox's sandbox_user, and the live-update event.
function syncTokensToStores(accessToken, refreshToken) {
  const prevUser = getTokenObj() || {};
  saveSession({ accessToken, refreshToken, userData: prevUser });

  const prevSandbox = decrypt(localStorage.getItem("sandbox_user")) || {};
  localStorage.setItem(
    "sandbox_user",
    encrypt({ ...prevSandbox, ACCESS_TOKEN: accessToken, REFRESH_TOKEN: refreshToken })
  );
  window.dispatchEvent(new Event("sandbox_user_update"));
}

// ── Lost-refresh-race recovery ──────────────────────────────────────────────
// With rotating refresh tokens, one session lineage is shared by several JS
// contexts (other portal tabs via localStorage; a standalone Sandbox tab via
// the shared cookies). This coordinator only dedupes refreshes INSIDE one
// page, so two contexts can race — the backend rotates for the winner and
// answers the loser with `recentDeletion` (surfaced as `isRefreshRaceLost`).
// The loser must NOT log out: the session is alive and the winner holds the
// fresh pair. Poll the sibling stores for a pair strictly NEWER (JWT iat) than
// the failed one, for the SAME user, and adopt it. Returns tokens or null.
const RACE_RECOVERY_ATTEMPTS = 3;
const RACE_RECOVERY_DELAY_MS = 700;

export async function adoptTokensFromSiblingContext(failedAccessToken) {
  for (let attempt = 0; attempt < RACE_RECOVERY_ATTEMPTS; attempt++) {
    // (a) Same-origin sibling tab: it wrote the fresh pair to localStorage.
    const stored = getTokenObj();
    if (
      stored?.ACCESS_TOKEN &&
      stored.ACCESS_TOKEN !== failedAccessToken &&
      isNewerToken(stored.ACCESS_TOKEN, failedAccessToken) &&
      tokenUserId(stored.ACCESS_TOKEN) === tokenUserId(failedAccessToken)
    ) {
      return { accessToken: stored.ACCESS_TOKEN, refreshToken: stored.REFRESH_TOKEN };
    }

    // (b) The other app (cross-origin): its refresh re-wrote the shared
    // cookies. Same-user + strictly-newer only — a DIFFERENT user's cookie is
    // a profile switch, owned by authSyncListener/bootstrap, not by recovery.
    const cookieAccess = getCookie("Authorization");
    const cookieRefresh = getCookie("refresh-token");
    if (
      cookieAccess &&
      cookieRefresh &&
      cookieAccess !== failedAccessToken &&
      isNewerToken(cookieAccess, failedAccessToken) &&
      tokenUserId(cookieAccess) === tokenUserId(failedAccessToken)
    ) {
      return { accessToken: cookieAccess, refreshToken: cookieRefresh };
    }

    // (c) The checkout/plugins MFE (cy-plugins): it shares this lineage via
    // the plain-JSON `cy-user` key and refreshes with its OWN client, so its
    // race win lands there first (it deliberately never touches the encrypted
    // `user` key). Same same-user + strictly-newer guards.
    try {
      const cyUser = JSON.parse(localStorage.getItem("cy-user") || "null");
      const pluginAccess = cyUser?.ACCESS_TOKEN;
      const pluginRefresh = cyUser?.REFRESH_TOKEN;
      if (
        pluginAccess &&
        pluginRefresh &&
        pluginAccess !== failedAccessToken &&
        isNewerToken(pluginAccess, failedAccessToken) &&
        tokenUserId(pluginAccess) === tokenUserId(failedAccessToken)
      ) {
        return { accessToken: pluginAccess, refreshToken: pluginRefresh };
      }
    } catch (_) {
      /* cy-user absent or not JSON — ignore */
    }

    // Winner may not have persisted yet — wait and re-check.
    if (attempt < RACE_RECOVERY_ATTEMPTS - 1) {
      await new Promise((resolve) => setTimeout(resolve, RACE_RECOVERY_DELAY_MS));
    }
  }
  return null;
}

export function doMfeTokenRefresh() {
  if (_mfeRefreshPromise) return _mfeRefreshPromise;

  _mfeRefreshPromise = (async () => {
    try {
      const tokenObj = getTokenObj();
      if (!tokenObj || !tokenObj.ACCESS_TOKEN) {
        // No local token → session is genuinely gone → log out (tag it so the
        // genuine-failure check below treats it as such, not as offline).
        throw Object.assign(new Error("No portal tokens available for refresh"), {
          refreshAuthFailed: true,
        });
      }

      let accessToken, refreshToken;
      try {
        ({ accessToken, refreshToken } = await refreshTokens(tokenObj));
      } catch (refreshErr) {
        // Lost refresh race → adopt the winning context's tokens instead of
        // failing. On no recovery, mark the error so callers don't repeat this
        // exact store-polling on the same page.
        if (!isRefreshRaceLost(refreshErr)) throw refreshErr;
        const adopted = await adoptTokensFromSiblingContext(tokenObj.ACCESS_TOKEN);
        if (!adopted) {
          refreshErr.raceRecoveryAttempted = true;
          throw refreshErr;
        }
        ({ accessToken, refreshToken } = adopted);
      }

      // Update portal tokens through the common saveSession so localStorage.user,
      // cy-user, profile_pic, and the shared cross-app cookies all stay in sync —
      // and sync to sandbox_user so the embedded Sandbox picks up new tokens
      // without an extra refresh (this coordinator runs only in MFE mode).
      syncTokensToStores(accessToken, refreshToken);

      return { accessToken, refreshToken };
    } catch (err) {
      // Log out only on a GENUINE refresh failure — the server responded
      // (401 / 500 / 200-valid:false) or there's no local token. A pure
      // connectivity error (offline / no `response`) keeps the session. Mark the
      // error so both apps' catch blocks skip their own sessionExpired() call
      // (no double dialogs / conflicting navigations).
      if (isGenuineRefreshFailure(err) && !err.sessionExpiredHandled) {
        sessionExpired();
        err.sessionExpiredHandled = true;
      }
      throw err;
    } finally {
      _mfeRefreshPromise = null;
    }
  })();

  return _mfeRefreshPromise;
}
